import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Check, Star, Crown, Play, Flame, Trophy, Swords, ChevronRight, Compass } from "lucide-react";
import api from "@/lib/api";
import { XpEconomyCard } from "@/components/XpEconomyCard";

export default function Training() {
  const [data, setData] = useState(null);
  const [presc, setPresc] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/training/path").then((r) => setData(r.data));
    api.get("/training/prescription").then((r) => setPresc(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="p-8 text-tc-muted font-mono text-sm">Loading the Trader Path…</div>;

  const p = data.progression;
  const open = (n) => { if (n.playable && n.status !== "LOCKED") navigate(`/training/${n.id}`); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">The Trader Path</h1>
          <p className="text-tc-sub mt-1">Your campaign from Market Rookie to Market Strategist. One mission at a time.</p>
        </div>
        <div className="flex gap-2">
          <Badge icon={Trophy} accent="text-tc-xp">{p.rank} · Lv {p.level}</Badge>
          {data.streak > 0 && <Badge icon={Flame} accent="text-tc-pass">{data.streak}-day streak</Badge>}
        </div>
      </div>

      <XpEconomyCard />

      {/* Today's Edge — deterministic training prescription */}
      {presc && (
        <div className="tc-card p-5 border-tc-coach/30 bg-gradient-to-r from-tc-coach/10 to-transparent" data-testid="prescription-card">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-tc-coach">
            <Compass className="w-4 h-4" /> Today's Edge
          </div>
          <div className="font-heading text-xl font-bold mt-1.5" data-testid="prescription-focus">{presc.focus_title}</div>
          <p className="text-tc-sub text-sm mt-1">{presc.strength_note}{presc.why}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {presc.plan.map((it, i) => (
              <span key={i} className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-tc-sub">{it.label}</span>
            ))}
            <span className="text-[11px] font-mono text-tc-muted">≈ {presc.estimated_minutes} min</span>
          </div>
        </div>
      )}

      {/* progress bar for the learner's current stage */}
      <div className="tc-card p-4" data-testid="stage-progress-card">
        <div className="flex justify-between text-xs font-mono text-tc-muted mb-1.5">
          <span data-testid="stage-progress-label">STAGE {data.current_stage.stage} · {data.current_stage.title}</span>
          <span data-testid="stage-progress-count">{data.current_stage.completed}/{data.current_stage.total} missions</span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div data-testid="stage-progress-bar" className="h-full bg-tc-coach rounded-full transition-all" style={{ width: `${(data.current_stage.completed / data.current_stage.total) * 100}%` }} />
        </div>
      </div>

      {data.stages.map((st) => (
        <div key={st.stage} className="relative">
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-xs font-mono uppercase tracking-widest px-2.5 py-1 rounded ${st.locked ? "bg-white/5 text-tc-muted" : "bg-tc-coach/15 text-tc-coach"}`}>
              Stage {st.stage} · {st.title}
            </span>
            {st.locked && <span className="text-[11px] text-tc-muted flex items-center gap-1"><Lock className="w-3 h-3" /> Unlocks at {STAGE_PREV(st, data)}</span>}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {st.nodes.map((n) => <MissionNode key={n.id} n={n} onOpen={() => open(n)} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function STAGE_PREV(st, data) {
  const prev = data.stages.find((s) => s.stage === st.stage - 1);
  return prev ? prev.rank_unlock : "";
}

function MissionNode({ n, onOpen }) {
  const boss = n.is_boss;
  const status = n.status;
  const locked = status === "LOCKED";
  const done = status === "COMPLETED" || status === "MASTERED";
  const current = status === "CURRENT";

  const border = boss ? "border-tc-xp/40" : current ? "border-tc-coach/60" : done ? "border-tc-bull/40" : "border-white/10";
  const glow = current ? "shadow-lg shadow-tc-coach/10" : boss && !locked ? "shadow-lg shadow-tc-xp/10" : "";

  return (
    <button data-testid={`mission-node-${n.id}`} onClick={onOpen} disabled={locked}
      className={`tc-card ${border} ${glow} p-4 text-left transition-all ${!locked ? "hover:-translate-y-0.5" : "opacity-60"} ${boss ? "bg-gradient-to-br from-tc-xp/10 to-transparent" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {boss ? <Swords className="w-4 h-4 text-tc-xp" /> : <span className="text-[11px] font-mono text-tc-muted">M{String(n.number).padStart(2, "0")}</span>}
          {current && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-tc-coach/20 text-tc-coach">CURRENT</span>}
        </div>
        <StatusIcon status={status} stars={n.stars} />
      </div>
      <div className={`font-heading font-bold mt-2 ${boss ? "text-tc-xp" : ""}`}>{n.name}</div>
      <div className="text-[13px] text-tc-sub mt-0.5">{locked && n.unlock_note ? n.unlock_note : n.tagline}</div>
      <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-tc-muted">
        <span className="text-tc-xp">+{n.xp} XP</span><span>· {n.est}</span>
        {n.skills?.length > 0 && <span>· {n.skills.length} skill{n.skills.length > 1 ? "s" : ""}</span>}
      </div>
      {current && (
        <div className="mt-3 flex items-center gap-1.5 text-tc-coach text-sm font-medium">
          {boss ? "Enter Boss Market" : "Start Mission"} <ChevronRight className="w-4 h-4" />
        </div>
      )}
    </button>
  );
}

function StatusIcon({ status, stars }) {
  if (status === "LOCKED") return <Lock className="w-4 h-4 text-tc-muted" />;
  if (status === "MASTERED") return <div className="flex gap-0.5">{[1, 2, 3].map((i) => <Star key={i} className={`w-3.5 h-3.5 ${i <= stars ? "text-tc-xp fill-tc-xp" : "text-tc-muted"}`} />)}</div>;
  if (status === "COMPLETED") return <div className="flex items-center gap-1"><Check className="w-4 h-4 text-tc-bull" /><div className="flex gap-0.5">{[1, 2, 3].map((i) => <Star key={i} className={`w-3 h-3 ${i <= stars ? "text-tc-xp fill-tc-xp" : "text-tc-muted"}`} />)}</div></div>;
  if (status === "CURRENT") return <Play className="w-4 h-4 text-tc-coach fill-tc-coach" />;
  return null;
}

function Badge({ icon: Icon, accent, children }) {
  return <span className={`text-xs font-mono px-3 py-1.5 rounded-full bg-white/5 border border-white/10 flex items-center gap-1.5 ${accent}`}><Icon className="w-3.5 h-3.5" /> {children}</span>;
}
